import React, { useCallback, useMemo, useState } from 'react';
import { Box, Container, CssBaseline, Grid, ThemeProvider } from '@mui/material';
import { useSelector } from 'react-redux';
import theme from '../../theme';
import { downloadCsv, buildDownloadName } from '../../utils/downloadCsv';
import { downloadXlsx } from '../../utils/downloadXlsx';
import { buildReplyDownloadRows } from '../../utils/replyDownloadRows';
import Topbar from '../Topbar';
import DashboardHeader from './DashboardHeader';
import OrganizationFilters from './OrganizationFilters';
import AgeSlider from './AgeSlider';
import CapacityMap from './CapacityMap';
import TalentDistribution from './TalentDistribution';
import { dashboardTokens as t } from './tokens';
import useDashboardData from '../../hooks/useDashboardData';
import { withLanguage } from '../common/Texts';

export default function Dashboard() {
    const company = useSelector((state) => state.user.company);
    const language = useSelector((state) => state.user.language);
    const tr = useMemo(() => withLanguage(language), [language]);

    const [filters, setFilters] = useState({
        slidePos: [],
        slideArea: [],
        slideBU: [],
        slideIG: [],
        slidePotential: [],
        slideAge: 0,
    });

    const {
        loading, pieData, levels, capPotencial, dots, chartDots, validReplies,
    } = useDashboardData(filters);

    const setFilter = (key) => (event) => {
        setFilters((prev) => ({ ...prev, [key]: event.target.value }));
    };

    const handleAgeChange = (_event, value) => {
        setFilters((prev) => ({ ...prev, slideAge: value }));
    };

    const handleClearAll = () => {
        setFilters((prev) => ({
            ...prev,
            slidePos: [],
            slideArea: [],
            slideBU: [],
            slideIG: [],
            slidePotential: [],
        }));
    };

    const handleDownload = useCallback((format = 'csv') => {
        // company starts as {} after logout/fresh login, so guard on the
        // arrays that the download actually needs, not just truthiness.
        if (!Array.isArray(company?.positions)) return;
        const data = buildReplyDownloadRows(validReplies, {
            company, language, tr, slideAge: filters.slideAge,
        });
        const fileName = buildDownloadName([company?.name, tr('capacity_map')], format);
        if (format === 'xlsx') {
            downloadXlsx(data, fileName);
        } else {
            downloadCsv(data, fileName);
        }
    }, [company, validReplies, language, tr, filters.slideAge]);

    const posRange = company?.positions ?? [];
    const areaRange = company?.departments ?? [];
    const buRange = company?.business_units ?? [];
    const igRange = company?.grupos_internos ?? [];

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline/>
            <Box sx={{ minHeight: '100vh', bgcolor: t.color.bgSub }}>
                <Topbar/>
                <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
                    <DashboardHeader language={language} company={company}/>

                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, md: 9 }}>
                            <OrganizationFilters
                                language={language}
                                buRange={buRange}
                                areaRange={areaRange}
                                posRange={posRange}
                                igRange={igRange}
                                slideBU={filters.slideBU}
                                slideArea={filters.slideArea}
                                slidePos={filters.slidePos}
                                slideIG={filters.slideIG}
                                slidePotential={filters.slidePotential}
                                onChangeBU={setFilter('slideBU')}
                                onChangeArea={setFilter('slideArea')}
                                onChangePos={setFilter('slidePos')}
                                onChangeIG={setFilter('slideIG')}
                                onChangePotential={setFilter('slidePotential')}
                                onClearAll={handleClearAll}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <AgeSlider
                                language={language}
                                value={filters.slideAge}
                                onChange={handleAgeChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 9 }}>
                            <CapacityMap
                                language={language}
                                company={company}
                                loading={loading}
                                chartDots={chartDots}
                                dots={dots}
                                onDownload={handleDownload}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <TalentDistribution
                                language={language}
                                loading={loading}
                                pieData={pieData}
                                levels={levels}
                                capPotencial={capPotencial}
                                colors={t.pieColors}
                            />
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </ThemeProvider>
    );
}
